/**
 * Fresh Fragrance wishlist — localStorage-backed, no app required.
 *
 * Elements:
 *  <ff-wishlist-toggle>  heart button on product cards + product page
 *  <ff-wishlist-count>   badge in the header (hidden when empty)
 *  <ff-wishlist-page>    renders the saved list on /pages/wishlist
 *
 * Stored shape (key ff:wishlist:v1): array of
 *  { handle, title, url, image, price, variantId, variantsCount, available }
 * price is in the store's minor unit (pence).
 */

const KEY = 'ff:wishlist:v1';
const CHANGE_EVENT = 'ff:wishlist:change';

function readList() {
  try {
    const data = JSON.parse(localStorage.getItem(KEY) || '[]');
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

function writeList(items) {
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    /* private browsing / quota — wishlist just won't persist */
  }
  broadcast();
}

function inList(handle) {
  return readList().some((item) => item.handle === handle);
}

function addItem(item) {
  const items = readList();
  if (!items.some((existing) => existing.handle === item.handle)) {
    items.unshift(item);
    writeList(items);
  }
}

function removeItem(handle) {
  writeList(readList().filter((item) => item.handle !== handle));
}

function broadcast() {
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { count: readList().length } }));
}

// Keep multiple tabs in sync.
window.addEventListener('storage', (event) => {
  if (event.key === KEY) broadcast();
});

function formatMoney(minorUnits) {
  const amount = Number(minorUnits);
  if (!Number.isFinite(amount)) return '';
  return `£${(amount / 100).toFixed(2)}`;
}

function escapeHtml(text) {
  return String(text ?? '').replace(
    /[&<>"']/g,
    (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]
  );
}

class FFWishlistToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', this.onClick);
    document.addEventListener(CHANGE_EVENT, this.sync);
    this.sync();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
    document.removeEventListener(CHANGE_EVENT, this.sync);
  }

  onClick = (event) => {
    // The whole product card is a link — don't navigate.
    event.preventDefault();
    event.stopPropagation();
    const { handle } = this.dataset;
    if (!handle) return;
    if (inList(handle)) {
      removeItem(handle);
    } else {
      addItem({
        handle,
        title: this.dataset.title || '',
        url: this.dataset.url || `/products/${handle}`,
        image: this.dataset.image || '',
        price: Number(this.dataset.price) || 0,
        variantId: this.dataset.variantId || '',
        variantsCount: Number(this.dataset.variantsCount) || 1,
        available: this.dataset.available !== 'false',
      });
    }
  };

  sync = () => {
    const active = inList(this.dataset.handle || '');
    this.classList.toggle('is-active', active);
    if (!this.button) return;
    this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
    const label = active ? this.button.dataset.labelRemove : this.button.dataset.labelAdd;
    if (label) this.button.setAttribute('aria-label', label);
    const text = this.button.querySelector('.ff-wishlist-toggle__label');
    if (text) {
      text.textContent = active ? text.dataset.textRemove || '' : text.dataset.textAdd || '';
    }
  };
}

class FFWishlistCount extends HTMLElement {
  connectedCallback() {
    document.addEventListener(CHANGE_EVENT, this.sync);
    this.sync();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.sync);
  }

  sync = () => {
    const count = readList().length;
    this.textContent = count > 99 ? '99+' : String(count);
    this.hidden = count === 0;
  };
}

class FFWishlistPage extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-ff-wl-grid]');
    this.emptyState = this.querySelector('[data-ff-wl-empty]');
    document.addEventListener(CHANGE_EVENT, this.onChange);
    this.render();
    this.refresh();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.onChange);
  }

  onChange = () => this.render();

  render() {
    if (!this.grid || !this.emptyState) return;
    const items = readList();
    this.emptyState.hidden = items.length > 0;
    this.grid.hidden = items.length === 0;
    this.grid.innerHTML = items.map((item) => this.cardHtml(item)).join('');

    this.grid.querySelectorAll('[data-ff-wl-remove]').forEach((button) => {
      button.addEventListener('click', () => removeItem(button.dataset.ffWlRemove));
    });
    this.grid.querySelectorAll('[data-ff-wl-add]').forEach((button) => {
      button.addEventListener('click', () => this.addToBag(button));
    });
  }

  cardHtml(item) {
    const title = escapeHtml(item.title);
    const url = escapeHtml(item.url);
    const soldOut = item.available === false;
    let action;
    if (soldOut) {
      action = '<button type="button" class="ff-wl-card__add" disabled>Sold out</button>';
    } else if (item.variantsCount > 1) {
      action = `<a class="ff-wl-card__add" href="${url}">Choose options</a>`;
    } else {
      action = `<button type="button" class="ff-wl-card__add" data-ff-wl-add="${escapeHtml(item.variantId)}">Add to bag</button>`;
    }
    return `
      <div class="ff-wl-card">
        <button
          type="button"
          class="ff-wl-card__remove"
          data-ff-wl-remove="${escapeHtml(item.handle)}"
          aria-label="Remove ${title} from wishlist"
        >&times;</button>
        <a class="ff-wl-card__media" href="${url}" tabindex="-1" aria-hidden="true">
          ${item.image ? `<img src="${escapeHtml(item.image)}" alt="" loading="lazy">` : ''}
        </a>
        <a class="ff-wl-card__title" href="${url}">${title}</a>
        <div class="ff-wl-card__price">${formatMoney(item.price)}</div>
        ${action}
      </div>`;
  }

  /** Re-fetch each saved product so price/availability stay honest. */
  async refresh() {
    const items = readList();
    if (items.length === 0) return;
    let changed = false;
    const updated = await Promise.all(
      items.map(async (item) => {
        try {
          const response = await fetch(`/products/${item.handle}.js`);
          if (response.status === 404) {
            // Product is gone — drop it from the list.
            changed = true;
            return null;
          }
          if (!response.ok) return item;
          const product = await response.json();
          const variant =
            product.variants.find((candidate) => candidate.available) || product.variants[0];
          const fresh = {
            ...item,
            title: product.title,
            url: `/products/${product.handle}`,
            image: product.featured_image || item.image,
            price: variant ? variant.price : product.price,
            variantId: variant ? String(variant.id) : item.variantId,
            variantsCount: product.variants.length,
            available: product.available,
          };
          if (JSON.stringify(fresh) !== JSON.stringify(item)) changed = true;
          return fresh;
        } catch {
          return item;
        }
      })
    );
    if (changed) {
      writeList(updated.filter(Boolean));
    }
  }

  async addToBag(button) {
    const variantId = button.dataset.ffWlAdd;
    if (!variantId) return;
    button.disabled = true;
    const originalText = button.textContent;
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
      });
      if (!response.ok) throw new Error('add failed');
      button.textContent = 'Added ✓';
      await this.updateCartBubble();
    } catch {
      button.textContent = 'Try again';
    } finally {
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 2000);
    }
  }

  async updateCartBubble() {
    try {
      const cart = await (await fetch('/cart.js')).json();
      document.querySelectorAll('.cart-bubble').forEach((bubble) => {
        bubble.classList.remove('visually-hidden');
        const count = bubble.querySelector('.cart-bubble__text-count');
        if (count) {
          count.classList.remove('hidden');
          count.textContent = cart.item_count < 100 ? String(cart.item_count) : '';
        }
      });
    } catch {
      /* count refreshes on next navigation */
    }
  }
}

if (!customElements.get('ff-wishlist-toggle')) {
  customElements.define('ff-wishlist-toggle', FFWishlistToggle);
}
if (!customElements.get('ff-wishlist-count')) {
  customElements.define('ff-wishlist-count', FFWishlistCount);
}
if (!customElements.get('ff-wishlist-page')) {
  customElements.define('ff-wishlist-page', FFWishlistPage);
}
